// Admin actions on a single user account, shared by the GhIPSS and bank
// Users pages. Each asks for confirmation, calls the backend, shows the
// result and calls `onDone` (e.g. reload the list) when it succeeded.
import axios from "./axios";

// User status codes from the backend
export const USER_STATUS = { ACTIVE: "1", LOCKED: "2", CHANGE_PASSWORD: "3" };

// Locked when status is 2 (or if the backend sends a locked flag)
export const isUserLocked = (user) =>
  String(user.status) === USER_STATUS.LOCKED ||
  [user.isLocked, user.islocked, user.locked].some(
    (v) => v === true || String(v).toLowerCase() === "true",
  );

// Status 3: has to change their password at the next sign-in
export const mustChangePassword = (user) =>
  String(user.status) === USER_STATUS.CHANGE_PASSWORD;

// The backend sends errorCode at the top level or inside generalResponse
const resultOf = (data = {}) => {
  const general = data.generalResponse || {};
  return {
    ok: String(data.errorCode ?? general.errorCode) === "0",
    message: data.errorMessage || general.errorMessage || data.message,
  };
};

async function confirmAndCall(vm, { title, text, confirmText, url, body, success, failure, onDone }) {
  const choice = await vm.$swal({
    title,
    text,
    icon: "warning",
    showCancelButton: true,
    confirmButtonText: confirmText,
    cancelButtonText: "Cancel",
    showCloseButton: true,
  });
  if (!choice.value) return;

  $.LoadingOverlay("show");
  try {
    const { ok, message } = resultOf((await axios.post(url, body)).data);
    if (ok) {
      vm.$swal("Success", success, "success");
      if (onDone) onDone();
    } else {
      vm.$swal("Failed", message || failure, "error");
    }
  } catch (err) {
    console.error(err);
    const { message } = resultOf(err.response && err.response.data);
    vm.$swal("Failed", message || failure, "error");
  } finally {
    $.LoadingOverlay("hide");
  }
}

export const lockUser = (vm, user, onDone) =>
  confirmAndCall(vm, {
    title: `Lock ( ${user.fullName} )`,
    text: "This user won't be able to sign in until their password is reset.",
    confirmText: "Yes, lock",
    url: `user/lock_user/${user.id}`,
    success: "User successfully locked!",
    failure: "User could not be locked. Try again!",
    onDone,
  });

export const resetUserPassword = (vm, user, onDone) =>
  confirmAndCall(vm, {
    title: `Reset password ( ${user.fullName} )`,
    text: `A temporary password will be emailed to ${user.emailAddress}.` +
      (isUserLocked(user) ? " This also unlocks the account." : ""),
    confirmText: "Yes, reset",
    url: `user/reset_user_password/${user.id}`,
    success: `A temporary password has been sent to ${user.emailAddress}.`,
    failure: "Password could not be reset. Try again!",
    onDone,
  });

const people = (n) => (n === 1 ? "1 user" : `${n} users`);

// Bulk versions: send the selected users' ids as a list. Users that are
// already locked are left out of a lock.
export function lockUsers(vm, users, onDone) {
  const toLock = users.filter((u) => !isUserLocked(u));
  if (toLock.length === 0) {
    vm.$swal("Nothing to lock", "The selected users are already locked.", "info");
    return;
  }
  const skipped = users.length - toLock.length;
  return confirmAndCall(vm, {
    title: `Lock ${people(toLock.length)}`,
    text: "They won't be able to sign in until their passwords are reset." +
      (skipped ? ` ${people(skipped)} already locked will be skipped.` : ""),
    confirmText: "Yes, lock",
    url: "user/lock_users",
    body: toLock.map((u) => u.id),
    success: `${people(toLock.length)} locked!`,
    failure: "Users could not be locked. Try again!",
    onDone,
  });
}

export function resetUserPasswords(vm, users, onDone) {
  const locked = users.filter(isUserLocked).length;
  return confirmAndCall(vm, {
    title: `Reset passwords for ${people(users.length)}`,
    text: "Each user will be emailed a temporary password." +
      (locked ? ` This also unlocks ${people(locked)}.` : ""),
    confirmText: "Yes, reset",
    url: "user/reset_user_passwords",
    body: users.map((u) => u.id),
    success: `Temporary passwords sent to ${people(users.length)}.`,
    failure: "Passwords could not be reset. Try again!",
    onDone,
  });
}
